/*
 * Claim Type area (audit Phase 3.3). Replaces wwwroot/js/Claim/ClaimType.js
 * with one module on the Phase 3.1 core. No jQuery.
 *
 * A ClaimType is the vocabulary entry: the Type string a claim carries, the
 * Value Type its values are drawn from, and the Use* flags saying which kinds
 * of row may carry it. It is edited from three places, which is why every
 * reload here has to work out where it was opened from:
 *   - the domain-wide list          (/ClaimType/Domain/{id}, #Domain_Id)
 *   - an Application's Resources tab (/ClaimType/Resource/{id}, #Resource_Id)
 *   - the Claim Type Audit page      (#ClaimTypeAudit_DomainId)
 *
 * Defects fixed in the move:
 *  - Delete reported failures into #ClaimTypeMessageContainer, an id no view
 *    renders. The containers are DomainClaimTypesMessageContainer and
 *    ResourceClaimTypesMessageContainer, so a refused delete (the usual case --
 *    the type is still in use) failed in complete silence. messageContainer()
 *    picks whichever the current scope rendered.
 *  - Every busy indicator was a bare `.fa-save` / `.fa-spinner` / `.fa-plus`
 *    document-wide selector, so saving a claim type blanked the save icon of
 *    every other open panel on the page. They are scoped now.
 *  - InitUseFlagsToggle was called from each modal's onSuccess hook, so the
 *    category radio and the IncludeInToken warning only worked on the paths
 *    that remembered to call it. It is an enhancer now (see
 *    enhanceClaimTypeForm) and runs over any swap that carries the form.
 *  - GetStandardTypes was dead: the Standard tab has loaded through
 *    data-tab-url since the tab strip moved to Bootstrap 5. Gone.
 */
import { ajaxHtml, ajaxJson, ajaxModal, ajaxSwap } from '../core/ajax';
import { all, one } from '../core/dom';
import { serialize } from '../core/forms';
import { modelStateSummary } from '../core/http';
import { confirm, hideModal } from '../core/modal';
import { toast } from '../core/toast';
import { isValid, setValidationModelState } from '../core/validation';

const MODAL = 'ClaimTypeModal';
const FORM = 'ClaimTypeForm';

function value(id: string): string | undefined {
    const node = document.getElementById(id) as HTMLInputElement | null;
    return node ? node.value : undefined;
}

function summaryOf(data: unknown): string {
    const modelState = (data as { modelState?: unknown } | null)?.modelState;
    return modelState ? modelStateSummary(modelState) : '';
}

function setHidden(selector: string, hidden: boolean): void {
    for (const node of all(selector)) node.classList.toggle('d-none', hidden);
}

/** Save icon <-> spinner, scoped to the modal rather than the whole document. */
function busy(working: boolean): void {
    setHidden(`#${MODAL} .fa-save`, working);
    setHidden(`#${MODAL} .fa-spinner`, !working);
}

/**
 * The alert host the current scope actually rendered. The domain list and the
 * resource tab name theirs differently and the audit page has neither, so a
 * single hard-coded id was wrong everywhere.
 */
function messageContainer(): string | undefined {
    for (const id of ['DomainClaimTypesMessageContainer', 'ResourceClaimTypesMessageContainer']) {
        if (document.getElementById(id)) return id;
    }
    return undefined;
}

interface EditModalOptions {
    url: string;
    errorTitle: string;
    busySelector?: string;
}

function openModal(options: EditModalOptions): void {
    if (options.busySelector) setHidden(options.busySelector, true);
    void ajaxModal({
        modal: MODAL,
        url: options.url,
        formId: FORM,
        focusId: 'ClaimType_Name',
        onError: (_xhr, _status, error) => {
            toast('danger', options.errorTitle, error);
            return true;   // else the modal is replaced by an error dialog
        },
        onComplete: () => {
            if (options.busySelector) setHidden(options.busySelector, false);
        },
    });
}

function saveClaimType(method: 'POST' | 'PUT', errorTitle: string): void {
    const form = one(`#${FORM}`) as HTMLFormElement | null;
    if (!form || !isValid(form)) return;
    busy(true);
    void ajaxJson({
        url: 'ClaimType',
        method,
        data: serialize(form),
        alertInto: 'ClaimTypeEditMessageContainer',
        errorTitle,
        onSuccess: () => {
            hideModal(MODAL);
            void ClaimType.ListReload();
        },
        onFailure: data => {
            const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
            if (modelState) setValidationModelState(modelState);
            return true;
        },
        onComplete: () => busy(false),
    });
}

/** The re-map the operator approved in a remediation dialog, or null. */
interface RemapChoice {
    valueType: string;
    fromValue: string;
    toValue: string;
    domainId: number;
    rows: unknown[];
}

function parseRows(id: string): unknown[] {
    const raw = (document.getElementById(id) as HTMLInputElement | null)?.value;
    if (!raw) return [];
    try {
        const parsed: unknown = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

/**
 * Reads the operator's choice out of the remediation dialog. Called after the
 * modal has hidden -- the shared confirm modal keeps its body until the next
 * confirm() call, so the inputs are still in the DOM.
 *
 * getAttribute, never a jQuery-style typed read: a vocabulary of numeric values
 * ("1", "2", "3") would come back as numbers and the Api rejects a body binding
 * those onto string properties. The value is operator-authored -- it is not safe
 * to assume it looks like a word.
 */
function readRemediateChoice(): RemapChoice | null {
    const choice = document.getElementById('ValueRemediateChoice');
    if (!choice) return null;

    const scoped = one('#ValueRemediateScope, input[name="ValueRemediateScope"]:checked') as HTMLInputElement | null;
    const rowsId = scoped?.value === 'all' ? 'ValueRemediateRowsAll' : 'ValueRemediateRowsOne';
    return {
        valueType: choice.getAttribute('data-value-type') ?? '',
        fromValue: choice.getAttribute('data-from-value') ?? '',
        toValue: value('ValueRemediateReplacement') ?? '',
        domainId: parseInt(choice.getAttribute('data-domain-id') ?? '', 10) || 0,
        rows: parseRows(rowsId),
    };
}

export const ClaimType = {
    Edit(claimTypeId: number | string): void {
        openModal({
            url: `ClaimType/${claimTypeId}/Edit`,
            errorTitle: `Failed to load Claim Type ${claimTypeId} for editing`,
            busySelector: `.claimtype-row[data-id="${claimTypeId}"] .fa-edit`,
        });
    },

    EditCancel(): void {
        hideModal(MODAL);
    },

    EditSave(): void {
        saveClaimType('PUT', 'Failed to save Claim Type changes');
    },

    NewDomainType(domainId: number | string): void {
        openModal({
            url: `ClaimType/Domain/${domainId}/Create`,
            errorTitle: 'Failed to render New Claim Type entry form',
            busySelector: '#NewClaimTypeButton',
        });
    },

    /**
     * Resource-scoped equivalent of NewDomainType. The Create endpoint
     * (Resource/{id}/Create) initializes ClaimType.ResourceId so the saved type
     * is private to that resource; the save and reload chain is shared.
     */
    NewResourceType(resourceId: number | string): void {
        openModal({
            url: `ClaimType/Resource/${resourceId}/Create`,
            errorTitle: 'Failed to render New Claim Type entry form',
            busySelector: '#NewClaimTypeButton',
        });
    },

    NewDomainTypeSave(): void {
        saveClaimType('POST', 'Failed to save new Claim Type');
    },

    Delete(claimTypeId: number | string): void {
        const rowSelector = `.claimtype-row[data-id="${claimTypeId}"]`;
        const name = document.getElementById(`Name${claimTypeId}`)?.textContent?.trim() ?? '';
        void confirm({
            title: 'Delete Claim Type',
            message: name ? `Delete claim type "${name}"?` : 'Delete this claim type?',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                setHidden(`${rowSelector} .fa-trash`, true);
                void ajaxJson({
                    url: `ClaimType/${claimTypeId}`,
                    method: 'DELETE',
                    busy: `${rowSelector} .fa-spinner`,
                    alertInto: messageContainer(),
                    errorTitle: 'Failed to delete Claim Type',
                    onSuccess: () => {
                        for (const row of all(rowSelector)) row.remove();
                    },
                    onComplete: () => setHidden(`${rowSelector} .fa-trash`, false),
                });
            },
        });
    },

    /**
     * Reloads whichever view hosted the edit, in priority order:
     *   #Resource_Id              -> the per-resource list inside Application Detail
     *   #Domain_Id                -> the domain-wide Claim Type list page
     *   #ClaimTypeAudit_DomainId  -> the Claim Type Audit page
     * No marker means no reload, which is what keeps an edit opened from an
     * audit row from fetching /ClaimType/Domain/undefined.
     */
    ListReload(): Promise<void> {
        const resourceId = value('Resource_Id');
        if (resourceId) {
            return ajaxSwap({
                container: 'ApplicationClaimTypesTab',
                url: `ClaimType/Resource/${resourceId}`,
                alertTitle: 'Failed to reload Claim Types',
            });
        }

        const domainId = value('Domain_Id');
        if (domainId !== undefined && domainId !== '') {
            return ajaxSwap({
                container: 'ViewContainer',
                url: `ClaimType/Domain/${domainId}`,
                onError: () => {
                    toast('danger', 'Failed to reload Claim Types');
                    return true;
                },
            });
        }

        const auditDomainId = value('ClaimTypeAudit_DomainId');
        if (auditDomainId !== undefined) {
            return ajaxSwap({
                container: 'ViewContainer',
                url: `ClaimType/Audit/${auditDomainId}`,
                onError: () => {
                    toast('danger', 'Failed to reload Claim Type Audit');
                    return true;
                },
            });
        }
        return Promise.resolve();
    },

    // ===== Retired-value remediation (the audit page's "Move..." affordance) =====
    //
    // A ValueNotDefined finding reports a legitimate administrative state: someone
    // retired a value from a list while grants still carried it, which is supported
    // and sometimes the entire point ("cut this integration off by deleting the
    // value"). Nothing here repairs anything automatically and "leave them alone"
    // stays a valid end state. What the warning lacked was an exit for the operator
    // who DID want the claims moved, short of opening every holder by hand.
    //
    // Re-map is the only action offered. Bulk revoke is absent by design: a deny row
    // inverts the grant, so deleting one grants access.
    RemediateValue(valueType: string, value: string, domainId: number, table: string, rowId: number): void {
        void ajaxHtml({
            url: 'ClaimType/Audit/Remediate',
            data: { valueType, value, domainId, table, rowId },
            alertTitle: 'Failed to check what carries this value',
            onSuccess: html => {
                // With nothing the operator can act on -- no replacement defined, or
                // every row outside their domains -- the dialog is a read: label the
                // button for that rather than offering an action that no-ops.
                const fragment = document.createElement('div');
                fragment.innerHTML = html;
                const actionable = fragment.querySelector('#ValueRemediateChoice')?.getAttribute('data-can-apply') === '1';
                void confirm({
                    title: 'Move Claims',
                    html,
                    confirmText: actionable ? 'Move' : 'Close',
                    variant: actionable ? 'warning' : 'primary',
                    onConfirm: () => ClaimType.ApplyRemediate(readRemediateChoice()),
                });
            },
        });
    },

    /**
     * Posts the re-map and reloads the audit so the moved rows drop off the report.
     * Unlike the retirement-time re-map in the Value Type editor this one is not
     * attached to another action -- it IS the action -- so a failure is reported
     * and nothing else runs.
     *
     * Shares the Api endpoint with the retirement-time path deliberately: one
     * re-map endpoint, one set of containment and skip rules. A second copy would
     * drift.
     */
    ApplyRemediate(choice: RemapChoice | null): void {
        if (!choice || !choice.toValue || choice.rows.length === 0) return;
        void ajaxJson({
            url: 'ClaimValueType/Value/Remap',
            method: 'POST',
            contentType: 'application/json',
            data: JSON.stringify(choice),
            onSuccess: data => {
                const result = (data as { data?: { applied?: number; messages?: string[] } } | null)?.data;
                const applied = result?.applied ?? 0;
                const messages = (result?.messages ?? []).join(' ');
                if (applied > 0) {
                    toast('success', `Moved ${applied} claim${applied === 1 ? '' : 's'} to ${choice.toValue}`, messages);
                } else {
                    toast('warning', 'No claims were moved', messages);
                }
                void ClaimType.ListReload();
            },
            onFailure: data => {
                toast('danger', 'Failed to move the claims', summaryOf(data));
                return true;
            },
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to move the claims', error);
                return true;
            },
        });
    },
};

/**
 * The Property/Claim category rule and the IncludeInToken warning, wired
 * whenever a Claim Type form lands in the page.
 *
 * An enhancer rather than a modal callback: the legacy InitUseFlagsToggle was
 * called from the Edit, NewDomainType and NewResourceType onSuccess hooks
 * individually, so the rule held only on the paths that remembered it.
 *
 * Category: the server rejects a type that mixes property-side and claim-side
 * flags (ClaimTypeController.ValidatePropertyClaimMutex); this is the UI half of
 * that rule. Checkboxes marked data-claimtype-locked="true" are skipped -- their
 * disabled+checked state is rendered by _UseFlags.cshtml from existing usage and
 * must survive a category toggle. They keep posting through the hidden mirror
 * input the partial renders alongside them.
 */
export function enhanceClaimTypeForm(root: ParentNode): void {
    const found = root instanceof Element && root.matches(`#${FORM}`)
        ? root
        : root.querySelector(`#${FORM}`);
    if (!(found instanceof HTMLFormElement)) return;
    const form = found;

    const applyCategory = (): void => {
        const side = (form.querySelector('input[name="ClaimType_Category"]:checked') as HTMLInputElement | null)?.value;
        for (const panel of form.querySelectorAll<HTMLElement>('[data-claimtype-panel]')) {
            const active = panel.getAttribute('data-claimtype-panel') === side;
            panel.style.opacity = active ? '1' : '0.5';
            for (const box of panel.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')) {
                if (box.getAttribute('data-claimtype-locked') === 'true') continue;
                box.disabled = !active;
                if (!active) box.checked = false;
            }
        }
    };
    for (const radio of form.querySelectorAll<HTMLInputElement>('input[name="ClaimType_Category"]')) {
        radio.addEventListener('change', applyCategory);
    }
    applyCategory();

    // IncludeInToken warning. Excluding a permission-kind claim type -- one whose
    // values are the REST actions McpAdmin authorizes against -- from tokens
    // disables that area's access for everyone. A blank ValueType defaults to REST
    // action, so it counts as permission-kind too. Warn when the box is UNCHECKED
    // for such a type.
    const includeInToken = form.querySelector<HTMLInputElement>('input[type="checkbox"][name="IncludeInToken"]');
    if (!includeInToken) return;
    const warning = document.getElementById('IncludeInTokenWarning');
    const restAction = includeInToken.getAttribute('data-restaction-valuetype') ?? '';
    const valueType = form.querySelector<HTMLSelectElement>('select[name="ClaimType.ValueType"]');

    const evaluate = (): void => {
        const selected = valueType?.value ?? '';
        const permissionKind = selected === '' || selected === restAction;
        warning?.classList.toggle('d-none', !(permissionKind && !includeInToken.checked));
    };
    includeInToken.addEventListener('change', evaluate);
    valueType?.addEventListener('change', evaluate);
    evaluate();
}
